import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import SideMenu from '../components/SideMenu';
import { Plus, Menu, Search, X } from 'lucide-react';
import { toast } from 'sonner';

const Vendors = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    short_code: '',
    mobile: '',
    specialization: ''
  });

  useEffect(() => {
    loadVendors();
  }, []);

  const loadVendors = async () => {
    try {
      const res = await axiosInstance.get('/vendors');
      setVendors(res.data);
    } catch (err) {
      console.error('Error loading vendors:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/vendors', formData);
      toast.success('Vendor added successfully!');
      setShowAddForm(false);
      setFormData({ name: '', short_code: '', mobile: '', specialization: '' });
      loadVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error adding vendor');
    }
  };

  // Filter vendors based on search query
  const filteredVendors = useMemo(() => {
    if (!searchQuery.trim()) return vendors;
    
    const query = searchQuery.toLowerCase();
    return vendors.filter(vendor => 
      vendor.name?.toLowerCase().includes(query) ||
      vendor.short_code?.toLowerCase().includes(query) ||
      vendor.mobile?.toLowerCase().includes(query) ||
      vendor.specialization?.toLowerCase().includes(query)
    );
  }, [vendors, searchQuery]);

  const effectiveRole = user.effective_role || user.role;
  if (effectiveRole !== 'Admin' && effectiveRole !== 'Controller') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
        <div className="page-container">
          <div className="text-center py-12">
            <p className="text-red-500 text-sm sm:text-base">Access Denied: Only Admin and Controller can manage vendors</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <div className="page-container">
        <div className="page-header">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setMenuOpen(true)} 
              className="p-2 hover:bg-amber-100 rounded-xl transition-colors touch-target"
              data-testid="open-menu-button"
            >
              <Menu className="w-6 h-6 text-gray-700" />
            </button>
            <h1 className="page-title" data-testid="vendors-page-title">Vendors</h1>
          </div>
        </div>

        {!showAddForm && (
          <div className="mb-4 sm:mb-6 space-y-3">
            <button
              onClick={() => setShowAddForm(true)}
              className="btn-primary flex items-center gap-2 w-full sm:w-auto"
              data-testid="add-vendor-button"
            >
              <Plus className="w-5 h-5" />
              Add Vendor
            </button>
            
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, code, mobile, or specialization..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-field w-full pl-10 pr-10"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            
            {/* Results count */}
            <p className="text-xs sm:text-sm text-gray-600">
              Showing {filteredVendors.length} of {vendors.length} vendors
            </p>
          </div>
        )}

        {showAddForm && (
          <div className="premium-card p-4 sm:p-6 mb-4 sm:mb-6 fade-in">
            <h2 className="text-base sm:text-lg font-semibold mb-4">Add New Vendor</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">Name *</label>
                <input
                  type="text"
                  className="input-field w-full"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="vendor-name-input"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">Short Code *</label>
                <input
                  type="text"
                  className="input-field w-full"
                  value={formData.short_code}
                  onChange={(e) => setFormData({ ...formData, short_code: e.target.value })}
                  required
                  data-testid="vendor-code-input"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">Mobile</label>
                <input
                  type="tel"
                  className="input-field w-full"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  data-testid="vendor-mobile-input"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">Specialization</label>
                <input
                  type="text"
                  className="input-field w-full"
                  value={formData.specialization}
                  onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                  data-testid="vendor-spec-input"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="btn-secondary flex-1"
                  data-testid="cancel-add-vendor"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1" data-testid="submit-vendor">
                  Add Vendor
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-amber-600 mx-auto"></div>
          </div>
        ) : filteredVendors.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-sm sm:text-base">
              {searchQuery ? 'No vendors found matching your search' : 'No vendors yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4 fade-in">
            {filteredVendors.map((vendor) => (
              <div key={vendor.id} className="premium-card p-4 sm:p-5" data-testid={`vendor-card-${vendor.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-bold text-base sm:text-lg gold-text">{vendor.name}</h3>
                    <span className="inline-block mt-2 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">
                      {vendor.short_code}
                    </span>
                  </div>
                </div>
                <div className="text-xs sm:text-sm text-gray-700 mt-3 space-y-1">
                  {vendor.mobile && <p><strong>Mobile:</strong> {vendor.mobile}</p>}
                  {vendor.specialization && <p><strong>Specialization:</strong> {vendor.specialization}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <SideMenu user={user} isOpen={menuOpen} onClose={() => setMenuOpen(false)} onLogout={onLogout} />
    </div>
  );
};

export default Vendors;
